import { Image, Text } from '@belong/ui';
import { UnitListingDetailsModel } from 'src/api/models';
import {
  INFO_ICONS,
  INFO_ICONS_SUBTITLES,
  INFO_ICONS_TITLES,
} from '../../constants';
import { HOUSE_PAGE_STRINGS } from '../../house-page.strings';
import { HomeLevelType } from '../../types';
import { getHeaterType, getLaundryType, getParking } from './utils';

function getInfoIconProps(
  src: string,
  title: string,
  subtitle: string,
): InfoIconProps {
  return { src, title, subtitle };
}

export function InfoIcons({ home }: InfoIconsProps): JSX.Element {
  const { basicInfo, propertyInfo, listingInfo, features } = home;
  const { detailBasic } = basicInfo || {};
  const { propertyType, subType } = propertyInfo!;
  const { isPetFriendly, anyPets, cats, dogs } = listingInfo?.petInfo || {};
  const homeFeatures = features!;

  const parking = getParking(homeFeatures);
  const airCondition = homeFeatures.find((feature) => feature.name === 'A/C');
  const isCentralAirCondition =
    airCondition &&
    (airCondition.attributes?.IsCentralAirCondition ||
      airCondition.attributes?.IsCentralSystem === 'yes');

  const typeData = {
    src: INFO_ICONS['type.single'],
    subtitle: INFO_ICONS_SUBTITLES['type.single'],
  };
  if (propertyType === 'MultiUnit') {
    typeData.src = INFO_ICONS['type.multi'];
    typeData.subtitle = INFO_ICONS_SUBTITLES['type.multi'];
  } else if (propertyType === 'Other') {
    typeData.src = INFO_ICONS['type.few'];
    typeData.subtitle = INFO_ICONS_SUBTITLES['type.other'];
  }
  if (propertyType !== 'SingleFamily' && subType) {
    typeData.subtitle = subType; // TODO: use Start Case
  }

  let coolingSubtitle = INFO_ICONS_SUBTITLES['cooling.no'];
  if (isCentralAirCondition) {
    coolingSubtitle = INFO_ICONS_SUBTITLES['cooling.central'];
  } else if (airCondition) {
    coolingSubtitle = INFO_ICONS_SUBTITLES['cooling.yes'];
  }

  const levelData = { src: '', subtitle: '' };
  if (detailBasic) {
    const unitLevel = detailBasic.unitLevel as HomeLevelType;
    if (unitLevel === 'Penthouse') {
      levelData.src = INFO_ICONS['level.penthouse'];
      levelData.subtitle = INFO_ICONS_SUBTITLES['level.penthouse'];
    } else {
      levelData.src = INFO_ICONS['level.default'];
      if (unitLevel === 'Ground Level') {
        levelData.subtitle = INFO_ICONS_SUBTITLES['level.ground'];
      } else if (unitLevel === 'Basement Level') {
        levelData.subtitle = INFO_ICONS_SUBTITLES['level.basement'];
      }
    }
  }

  let petSubtitle = '';
  if (!isPetFriendly) {
    petSubtitle = INFO_ICONS_SUBTITLES['pets.no'];
  } else if (anyPets) {
    petSubtitle = INFO_ICONS_SUBTITLES['pets.any'];
  } else if (dogs) {
    petSubtitle = INFO_ICONS_SUBTITLES['pets.dogs'];
  } else if (cats) {
    petSubtitle = INFO_ICONS_SUBTITLES['pets.cats'];
  }

  const defaultParkingSubtitle =
    parking.type || HOUSE_PAGE_STRINGS['infoIcons.noParking'];
  const parkingSpotsSubtitle = parking.spots
    ? `${parking.spots} Spot${parking.spots > 1 ? 's' : ''}`
    : defaultParkingSubtitle;
  const petsIcon = isPetFriendly
    ? INFO_ICONS['pets.yes']
    : INFO_ICONS['pets.no'];

  const iconProps: InfoIconProps[] = [
    getInfoIconProps(typeData.src, INFO_ICONS_TITLES.type, typeData.subtitle),
    ...(levelData.src && levelData.subtitle
      ? [
          getInfoIconProps(
            levelData.src,
            INFO_ICONS_TITLES.level,
            levelData.subtitle,
          ),
        ]
      : []),
    getInfoIconProps(
      INFO_ICONS.parking,
      INFO_ICONS_TITLES.parking,
      parkingSpotsSubtitle,
    ),
    ...(petSubtitle
      ? [getInfoIconProps(petsIcon, INFO_ICONS_TITLES.pets, petSubtitle)]
      : []),
    getInfoIconProps(
      INFO_ICONS.cooling,
      INFO_ICONS_TITLES.heat,
      getHeaterType(homeFeatures) || INFO_ICONS_SUBTITLES['heat.no'],
    ),
    getInfoIconProps(
      INFO_ICONS.heat,
      INFO_ICONS_TITLES.cooling,
      coolingSubtitle,
    ),
    getInfoIconProps(
      INFO_ICONS.laundry,
      INFO_ICONS_TITLES.laundry,
      getLaundryType(homeFeatures) || INFO_ICONS_SUBTITLES['laundry.no'],
    ),
  ];

  return (
    <div className="gap-y-sm grid grid-cols-2 md:grid-cols-3">
      {iconProps.map((props, idx) => (
        <InfoIcon {...props} key={idx} />
      ))}
    </div>
  );
}

type InfoIconsProps = {
  home: UnitListingDetailsModel;
};

function InfoIcon({ src, title, subtitle }: InfoIconProps): JSX.Element {
  return (
    <div className="flex flex-col items-start lg:flex-row lg:items-center">
      <div className="mr-xs" style={{ width: 72, height: 72 }}>
        <Image src={src} alt="" />
      </div>
      <div>
        <Text fontWeight="semibold">{title}</Text>
        <Text>{subtitle}</Text>
      </div>
    </div>
  );
}

type InfoIconProps = {
  src: string;
  title: string;
  subtitle: string;
};
